import React, { useState } from "react";
import PropTypes from "prop-types";
import { Container, Box, Grid, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { getCrmOrdersTrend, getCrmOrdersRefunds } from "../../api";
import { useAnalyticsFetch } from "./hooks/useAnalyticsFetch";
import LineChartCard from "./components/LineChartCard";
import KpiCard from "./components/KpiCard";
import { formatStatusLabel, getStatusColor } from "./components/orderStatusMap";

const GROUP_BY_OPTIONS = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

const Orders = ({ filter, startDate, endDate }) => {
  const [groupBy, setGroupBy] = useState("day");

  const { data: trendData, loading: trendLoading } = useAnalyticsFetch(getCrmOrdersTrend, [
    filter,
    startDate,
    endDate,
    groupBy,
  ]);

  const { data: refundData, loading: refundLoading } = useAnalyticsFetch(getCrmOrdersRefunds, [
    filter,
    startDate,
    endDate,
    groupBy,
  ]);

  const trendDatasets = trendData?.trend?.series
    ? Object.values(trendData.trend.series).map((series, index) => ({
        label: formatStatusLabel(series.name),
        data: series.data,
        color: getStatusColor(series.name, index),
      }))
    : [];

  return (
    <Container sx={{ paddingLeft: "0 !important", paddingRight: "0 !important" }}>
      <Box sx={{ marginBottom: "1rem" }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="orders-groupby-label">Group By</InputLabel>
          <Select
            labelId="orders-groupby-label"
            label="Group By"
            value={groupBy}
            onChange={(event) => setGroupBy(event.target.value)}
          >
            {GROUP_BY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ marginBottom: "1rem" }}>
        <LineChartCard
          title="Orders Trend by Status"
          labels={trendData?.trend?.labels || []}
          datasets={trendDatasets}
          loading={trendLoading}
        />
      </Box>

      <Grid container spacing={2} sx={{ marginBottom: "1rem" }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Refund Count" value={refundData?.refund_count ?? 0} color="#c0392b" loading={refundLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Refund Amount" value={refundData?.refund_amount ?? 0} color="#c0392b" currency loading={refundLoading} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Refund Rate (Orders)"
            value={refundData?.refund_rate_by_order_count != null ? `${refundData.refund_rate_by_order_count}%` : "0%"}
            color="#94660c"
            loading={refundLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Refund Rate (Revenue)"
            value={refundData?.refund_rate_by_revenue != null ? `${refundData.refund_rate_by_revenue}%` : "0%"}
            color="#94660c"
            loading={refundLoading}
          />
        </Grid>
      </Grid>

      <LineChartCard
        title="Refund Trend"
        labels={refundData?.trend?.labels || []}
        datasets={refundData?.trend ? [{ label: "Refunds", data: refundData.trend.series }] : []}
        loading={refundLoading}
      />
    </Container>
  );
};

Orders.propTypes = {
  filter: PropTypes.string.isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
};

export default Orders;
